import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PageHero } from "@/components/Shared";

export const PHOTOS = [
  { src: "/brand/rakim-portrait.webp", alt: "Rakim smiling with his basketball", cat: "Rakim" },
  { src: "/brand/rakim-smile.webp", alt: "Rakim's big smile", cat: "Rakim" },
  { src: "/gallery/rakim-mom-selfie.webp", alt: "Rakim and his mom, cheek to cheek", cat: "Rakim" },
  { src: "/gallery/mom-roses.webp", alt: "Rakim's mother holding his portrait", cat: "Family & Mission" },
  { src: "/gallery/mom-chair-1.webp", alt: "Rakim's mother with his portrait and bike", cat: "Family & Mission" },
  { src: "/gallery/mom-tribute.webp", alt: "Rakim's mother in front of his portrait", cat: "Family & Mission" },
  { src: "/gallery/mom-chair-2.webp", alt: "Rakim's mother seated with his portrait and bike", cat: "Family & Mission" },
  { src: "/gallery/mom-standing.webp", alt: "Rakim's mother holding his photo", cat: "Family & Mission" },
  { src: "/gallery/mom-bike-flowers.webp", alt: "Rakim's mother with flowers and his bike", cat: "In Loving Memory" },
  { src: "/brand/rakim-memorial.webp", alt: "Loved ones honoring Rakim Akbari", cat: "In Loving Memory" },
  { src: "/gallery/memorial-balloons.webp", alt: "Family gathered with blue balloons to honor Rakim", cat: "In Loving Memory" },
  { src: "/brand/walk-flyer.webp", alt: "2nd Annual Swim for Rakim Walk flyer", cat: "Events & Flyers" },
  { src: "/brand/infographic.webp", alt: "Turning Tragedy Into Action poster", cat: "Events & Flyers" },
  { src: "/brand/poster.webp", alt: "Swim for Rakim water safety infographic", cat: "Events & Flyers" },
];
const CATS = ["All", ...new Set(PHOTOS.map((p) => p.cat))];

function Lightbox({ list, index, setIndex }) {
  const go = useCallback((d) => setIndex((i) => (i + d + list.length) % list.length), [list.length, setIndex]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);
  const p = list[index];
  return (
    <Dialog open onOpenChange={() => setIndex(null)}>
      <DialogContent className="max-w-5xl bg-[#03045E] border-0 p-3 sm:p-6 rounded-2xl [&>button]:hidden" data-testid="gallery-lightbox">
        <DialogTitle className="sr-only">{p.alt}</DialogTitle>
        <div className="absolute top-3 right-3 z-10"><button onClick={() => setIndex(null)} className="w-10 h-10 rounded-full bg-white text-[#03045E] flex items-center justify-center shadow" data-testid="gallery-close"><X className="w-5 h-5" /></button></div>
        <img src={p.src} alt={p.alt} className="w-full max-h-[78vh] object-contain rounded-xl" data-testid="gallery-lightbox-image" />
        <div className="flex items-center justify-between text-white mt-3">
          <button onClick={() => go(-1)} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="gallery-prev"><ChevronLeft /></button>
          <p className="text-sm text-sky-100 text-center">{p.alt} <span className="text-sky-300">• {index + 1}/{list.length}</span></p>
          <button onClick={() => go(1)} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="gallery-next"><ChevronRight /></button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Gallery() {
  const [cat, setCat] = useState("All");
  const [index, setIndex] = useState(null);
  const list = PHOTOS.filter((p) => cat === "All" || p.cat === cat);
  return (
    <div data-testid="gallery-page">
      <PageHero eyebrow="Gallery" title="Moments that keep his smile alive" script="His life was a gift. His legacy is saving lives." />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex flex-wrap gap-2 mb-8">
          {CATS.map((c) => <button key={c} onClick={() => setCat(c)} data-testid={`gallery-filter-${c.split(" ")[0].toLowerCase()}`} className={`rounded-full px-5 py-2 text-xs font-bold uppercase transition-colors duration-200 ${cat === c ? "bg-[#03045E] text-white" : "bg-white border border-sky-200 text-[#03045E] hover:bg-sky-50"}`}>{c}</button>)}
        </div>
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [&>*]:mb-4">
          {list.map((p, i) => (
            <motion.button key={p.src} onClick={() => setIndex(i)} data-testid={`gallery-item-${i}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }}
              className="block w-full break-inside-avoid rounded-2xl overflow-hidden bg-white shadow-sm group relative">
              <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-auto transition-transform duration-500 group-hover:scale-105" />
              <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#03045E]/80 to-transparent text-white text-xs font-semibold text-left p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">{p.alt}</span>
            </motion.button>
          ))}
        </div>
      </section>
      {index !== null && <Lightbox list={list} index={index} setIndex={setIndex} />}
    </div>
  );
}
